import { createStore } from 'vuex';
import axios from 'axios';
import user from "@/store/user.store";
import selection from "@/store/selection.store";
import collections from "@/store/collections.store";
import { entityConfigs } from '@/entityConfigs';
import { facetsByCategory } from '@/facetConfigUtils';
import { urlBase, axiosConfig } from '@/apiConfig';
import { api } from '@/api';

// Conversion: 1 credit = $0.0001 (10,000 credits = $1)
const CREDIT_TO_USD = 0.0001;

export function formatUsd(dollars, decimals = 3) {
    if (dollars == null) return '$0.' + '0'.repeat(decimals);
    return '$' + Number(dollars).toFixed(decimals);
}

export function creditsToUsd(credits) {
    if (credits == null) return 0;
    return Math.round(credits * CREDIT_TO_USD * 10000) / 10000;
}

const stateDefaults = function () {
    const ret = {
        entityType: "works",
        activeFilter: null,
        activeFilterKey: null,
        newFilterKey: null,
        filterObjects: [],
        appliedFilterObjects: [],
        textSearch: "",
        page: 1,
        results: [],
        resultsObject: null,
        sort: null,
        responseTime: null,
        resultsCount: null,
        isLoading: false,
        snackbarIsOpen: false,
        snackbarMsg: "",
        snackbarIcon: null,
        snackbarColor: undefined,
        zoomId: null,
        apiDialogIsOpen: true,
        apiDialogUrl: "",
        showFiltersDrawer: false,
        facetZoom: null,
        facetsListDialogIsOpen: false,
        exportProgressUrl: null,
        filterOptionChipOpenMenu: null,
        isApiEditorShowing: false,
        useV2: false,
        // SERP page size (results per page). List and table view keep INDEPENDENT
        // sizes so changing one never bleeds into the other (table wants dense
        // pages, list wants fewer tall cards). Persisted to localStorage; list
        // defaults 10, table defaults 100. url.js getPerPage picks by current view;
        // a deep-link `per_page` is adopted into the active view's size as a session
        // override (persist:false) without overwriting the saved preference.
        serpPageSize: parseInt(localStorage.getItem('serp-page-size'), 10) || 10,
        serpTablePageSize: parseInt(localStorage.getItem('serp-table-page-size'), 10) || 100,
        isInitialLoad: true, // used to for bypassing cache on freshloads
        // Centralized query object - fetched once on page load, used by all view modes
        queryObject: null,
        queryObjectLoading: false,
        queryObjectError: null,
        // Structured validation error from the last OQL submit (#373), surfaced
        // inline in the OQL panel. Shape: {valid:false, errors:[{message,position,…}]}.
        oqlSubmitError: null,
        showEntityPageStats: true, // show metric rows + group-bys + works section on entity pages
        plans: [], // available plans loaded at app boot
        defaultApiMaxPerDay: 10000, // default credits per day for users without a plan
        rateLimitData: null,
        rateLimitLastFetchedAt: null,
        featureFlags: {},
        creditLimitDialogIsOpen: false,
    }
    return ret;
}


// Clear per-user store modules when the user logs out. Without this,
// signing in as user B in the same tab briefly renders user A's collections
// until something dispatches collections/fetchAll (security review M7).
const clearPerUserStateOnLogout = (store) => {
    store.subscribe((mutation) => {
        if (mutation.type === 'user/logout') {
            store.commit('collections/clear');
        }
    });
};

export default createStore({
    state: stateDefaults(),
    modules: {
        user,
        selection,
        collections,
    },
    plugins: [clearPerUserStateOnLogout],
    mutations: {
        setApiDialogUrl(state, url) {
            state.apiDialogUrl = url;
        },
        snackbar(state, arg) {
            state.snackbarIsOpen = true;
            if (typeof arg === "string") {
                state.snackbarMsg = arg;
            } else {
                state.snackbarMsg = arg.msg;
                state.snackbarColor = arg.color;
            }
        },
        closeSnackbar(state) {
            state.snackbarMsg = "";
            state.snackbarIsOpen = false;
            state.snackbarColor = undefined;
        },
        showCreditLimitDialog(state) {
            state.creditLimitDialogIsOpen = true;
        },
        closeCreditLimitDialog(state) {
            state.creditLimitDialogIsOpen = false;
        },
        setGlobalIsLoading(state, isLoading) {
            state.isLoading = !!isLoading;
        },
        toggleFiltersDrawer(state) {
            state.showFiltersDrawer = !state.showFiltersDrawer;
        },
        setZoomId(state, id) {
            state.zoomId = id;
        },
        setEntityType(state, entityType) {
            state.entityType = entityType;
        },
        setIsInitialLoad(state, isInitialLoad) {
            state.isInitialLoad = isInitialLoad;
        },
        setUseV2(state, value) {
            state.useV2 = value;
        },
        setShowEntityPageStats(state, value) {
            state.showEntityPageStats = value;
        },
        // persist:true for an explicit user pick (writes the durable preference);
        // persist:false for a session-only override adopted from a URL `per_page`.
        setSerpPageSize(state, { value, persist }) {
            state.serpPageSize = value;
            if (persist) localStorage.setItem('serp-page-size', String(value));
        },
        setSerpTablePageSize(state, { value, persist }) {
            state.serpTablePageSize = value;
            if (persist) localStorage.setItem('serp-table-page-size', String(value));
        },
        setQueryObject(state, queryObject) {
            state.queryObject = queryObject;
            state.queryObjectError = null;
        },
        setQueryObjectLoading(state, loading) {
            state.queryObjectLoading = loading;
        },
        setQueryObjectError(state, error) {
            state.queryObjectError = error;
            state.queryObject = null;
        },
        clearQueryObject(state) {
            state.queryObject = null;
            state.queryObjectError = null;
            state.queryObjectLoading = false;
        },
        setOqlSubmitError(state, error) {
            state.oqlSubmitError = error;
        },
        setPlans(state, plans) {
            state.plans = plans;
        },
        setDefaultApiMaxPerDay(state, value) {
            state.defaultApiMaxPerDay = value;
        },
        setRateLimitData(state, data) {
            state.rateLimitData = data;
            state.rateLimitLastFetchedAt = Date.now();
        },
        setFeatureFlags(state, flagNames) {
            const flags = {};
            for (const name of flagNames) {
                flags[name] = true;
            }
            // Merge in locally-persisted flags (for UI-defined flags not yet in backend)
            try {
                const localFlags = JSON.parse(localStorage.getItem('localFeatureFlags') || '[]');
                for (const name of localFlags) {
                    flags[name] = true;
                }
            } catch (e) { /* ignore parse errors */ }
            state.featureFlags = flags;
        },
    },
    actions: {
        async fetchPlans({ commit }) {
            try {
                const res = await axios.get(
                    `${urlBase.userApi}/plans`,
                    axiosConfig({ userAuth: true })
                );
                commit('setPlans', res.data.results || []);
                if (res.data.meta?.default_api_max_per_day) {
                    commit('setDefaultApiMaxPerDay', res.data.meta.default_api_max_per_day);
                }
            } catch (e) {
                console.error('Failed to fetch plans:', e);
            }
        },
        async fetchRateLimitData({ commit, state }, options) {
            const fresh = options?.fresh;
            const apiKey = state.user?.apiKey;
            if (!apiKey) return;
            try {
                const url = fresh
                    ? `${urlBase.api}/rate-limit?fresh=1`
                    : `${urlBase.api}/rate-limit`;
                const resp = await axios.get(url, axiosConfig());
                commit('setRateLimitData', resp.data?.rate_limit || null);
            } catch (e) {
                console.warn('Failed to fetch rate limit data:', e);
            }
        },
        async fetchQueryObject({ commit }, { entityType, query }) {
            commit('setQueryObjectLoading', true);
            try {
                const response = await api.getQuery({
                    entity_type: entityType,
                    query: query || {},
                });
                commit('setQueryObject', response);
            } catch (e) {
                // /query returns 400 with a structured validation message on a bad
                // query — surface that, not the generic axios "status code 400".
                const msg = e.response?.data?.validation?.errors?.[0]?.message
                    || e.response?.data?.message
                    || e.message
                    || 'Failed to fetch query';
                commit('setQueryObjectError', msg);
            } finally {
                commit('setQueryObjectLoading', false);
            }
        },
    },
    getters: {
        apiDialogUrl(state) {
            return state.apiDialogUrl;
        },
        entityType(state) {
            return state.entityType;
        },
        entityConfig(state) {
            return entityConfigs[state.entityType];
        },
        showFiltersDrawer(state) {
            return state.showFiltersDrawer;
        },
        facetZoom(state) {
            return state.facetZoom;
        },
        globalIsLoading(state) {
            return state.isLoading;
        },
        results(state) {
            return state.results;
        },
        resultsCount(state) {
            return state?.resultsObject?.meta?.count;
        },
        // adds stuff just used by the ui
        textSearch(state) {
            return state.textSearch;
        },
        zoomId(state) {
            return state.zoomId;
        },
        filtersZoom(state) {
            return state.filtersZoom
        },
        facetsByCategory(state) {
            return function (searchString) {
                return facetsByCategory(state.entityType, searchString);
            };
        },
        isLocalEnv() {
            return window.location.hostname === "localhost";
        },
        isStagingEnv() {
            return window.location.hostname === "staging.openalex.org";
        },
        isProductionEnv() {
            return window.location.hostname === "openalex.org";
        },
        environment(getters) {
            if (getters.isLocalEnv) {
                return "local";
            } else if (getters.isStagingEnv) {
                return "staging";
            } else if (getters.isProductionEnv) {
                return "production";
            } else {
                return "unknown";
            }
        },
        plans(state) {
            return state.plans;
        },
        queryObject(state) {
            return state.queryObject;
        },
        queryObjectLoading(state) {
            return state.queryObjectLoading;
        },
        queryObjectError(state) {
            return state.queryObjectError;
        },
        oqlSubmitError(state) {
            return state.oqlSubmitError;
        },
        rateLimitData(state) {
            return state.rateLimitData;
        },
        defaultDailyBudgetUsd(state) {
            return creditsToUsd(state.defaultApiMaxPerDay);
        },
        featureFlags(state) {
            const flags = { ...state.featureFlags };
            delete flags.noviceMode;
            return flags;
        },
    },
})
